/**
 * Réplica exacta de Dashboard!C6:C17 (y del estado F) sobre los valores YA calculados por la hoja.
 * - Sin filtro (boards = null) se usan TODAS las filas, como la hoja → los valores coinciden exactamente.
 * - Con filtro, los registros se unen a OT-01 por código de tablero (SEG-01 e INS-01 son globales).
 * Funciones puras: sin DOM ni red.
 */
import { colToIndex } from '../a1';
import { isBlank, isNumber, sheetsEquals } from '../formulas/sheets-semantics';
import type { KpiId, SheetName } from '../schema';
import { KPI_ROWS } from '../schema';
import type { CellValue } from '../workbook';
import { GLOBAL_KPIS, KPI_FORMAT } from './kpi-formulas';
import { boardKey } from './snapshot';

export type DataRow = { r: number; v: CellValue[] };
export type RowsBySheet = Partial<Record<SheetName, DataRow[]>>;

export type KpiStatus = 'ok' | 'bad' | 'nodata' | 'nogoal';

export interface KpiResult {
  id: KpiId;
  row: number;
  /** '' = sin datos (como el "" de IFERROR en la hoja). */
  value: number | '';
  status: KpiStatus;
  format: 'percent' | 'money' | 'int';
  global: boolean;
}

/** Etiquetas del semáforo (decisión P5: "Revisar" de la hoja se muestra como "No cumple"). */
export const KPI_STATUS_LABEL: Record<KpiStatus, string> = {
  ok: 'Cumple',
  bad: 'No cumple',
  nodata: 'Sin datos',
  nogoal: 'Definir meta',
};

const get = (row: DataRow, col: string): CellValue => row.v[colToIndex(col)] ?? '';
const eq = (row: DataRow, col: string, crit: string) => sheetsEquals(get(row, col), crit);

/** Filas de la hoja que pertenecen al conjunto de tableros (null = todas). */
export function inBoards(rows: DataRow[] | undefined, codeCol: string, boards: Set<string> | null): DataRow[] {
  if (!rows) return [];
  if (!boards) return rows;
  return rows.filter((row) => boards.has(boardKey(get(row, codeCol))));
}

const count = (rows: DataRow[], pred: (r: DataRow) => boolean) => rows.reduce((n, r) => n + (pred(r) ? 1 : 0), 0);
const sum = (rows: DataRow[], col: string, pred: (r: DataRow) => boolean = () => true) =>
  rows.reduce((s, r) => {
    const v = get(r, col);
    return s + (pred(r) && isNumber(v) ? v : 0);
  }, 0);
/** IFERROR(a/b,"") */
const ratio = (a: number, b: number): number | '' => (b === 0 || !Number.isFinite(a / b) ? '' : a / b);

export function computeKpiValues(data: RowsBySheet, boards: Set<string> | null): Record<KpiId, number | ''> {
  const ot = inBoards(data['OT-01'], 'A', boards);
  const cos = inBoards(data['COS-01'], 'A', boards);
  const prd = inBoards(data['PRD-01'], 'B', boards);
  const des = inBoards(data['DES-01'], 'A', boards);
  const ncr = inBoards(data['NCR-01'], 'C', boards);
  const seg = data['SEG-01'] ?? [];
  const ins = data['INS-01'] ?? [];
  const yes = count(ot, (r) => eq(r, 'Q', 'Sí'));
  const no = count(ot, (r) => eq(r, 'Q', 'No'));
  return {
    onTimeDelivery: ratio(yes, yes + no),
    firstPassYield: ratio(
      count(ot, (r) => eq(r, 'S', 'Sí') && eq(r, 'R', 'Sí')),
      count(ot, (r) => eq(r, 'R', 'Sí')),
    ),
    productionEfficiency: ratio(sum(ot, 'O'), sum(ot, 'N')),
    materialsDeviation: sum(cos, 'F'),
    reworkRatio: ratio(
      sum(prd, 'G', (r) => eq(r, 'H', 'Sí')),
      sum(prd, 'G'),
    ),
    documentTraceability: ratio(
      count(ot, (r) => eq(r, 'K', 'Entregado') && eq(r, 'T', 'Sí')),
      count(ot, (r) => eq(r, 'K', 'Entregado')),
    ),
    releasesWithPendingTests: count(des, (r) => !isBlank(get(r, 'M')) && eq(r, 'D', 'No')),
    safetyIncidents: count(seg, (r) => eq(r, 'C', 'Incidente')),
    openUnsafeConditions: count(seg, (r) => eq(r, 'C', 'Condición insegura') && eq(r, 'N', 'Abierta')),
    unfitInstruments: count(ins, (r) => ['Vencido', 'No usar', 'Sin calibración verificable'].some((s) => eq(r, 'M', s))),
    openNcr: count(ncr, (r) => eq(r, 'N', 'Abierta') || eq(r, 'N', 'En corrección')),
    profitMargin: ratio(sum(cos, 'O'), sum(cos, 'C')),
  };
}

/** Dashboard!F: =IF(C="","Sin datos",IF(E="","Definir meta",IF(IF(D="≥",C>=E,C<=E),"Cumple","Revisar"))) */
export function kpiStatus(value: CellValue, direction: CellValue, goal: CellValue): KpiStatus {
  if (isBlank(value)) return 'nodata';
  if (isBlank(goal)) return 'nogoal';
  const v = Number(value);
  const g = Number(goal);
  const ok = direction === '≥' ? v >= g : v <= g;
  return ok ? 'ok' : 'bad';
}

export function computeKpis(
  data: RowsBySheet,
  boards: Set<string> | null,
  goals: Partial<Record<KpiId, { direction: CellValue; goal: CellValue }>>,
): KpiResult[] {
  const values = computeKpiValues(data, boards);
  return KPI_ROWS.map(({ row, id }) => ({
    id,
    row,
    value: values[id],
    status: kpiStatus(values[id], goals[id]?.direction ?? '', goals[id]?.goal ?? ''),
    format: KPI_FORMAT[id],
    global: GLOBAL_KPIS.includes(id),
  }));
}

/** Mapeo del texto de la hoja (F) a la clave del semáforo. */
export function statusFromSheet(text: string): KpiStatus {
  if (text === 'Cumple') return 'ok';
  if (text === 'Revisar' || text === 'No cumple') return 'bad';
  if (text === 'Definir meta') return 'nogoal';
  return 'nodata';
}

/**
 * Criterio 1: con filtros vacíos, los KPIs del código deben ser IDÉNTICOS a Dashboard!C6:C17.
 * Compara contra los valores de la hoja (sheetValue); omite los KPIs restringidos.
 */
export function kpiParity(
  data: RowsBySheet,
  dashboard: Array<{ id: KpiId; sheetValue: CellValue; restricted: boolean }>,
): Array<{ id: KpiId; sheet: CellValue; code: number | ''; equal: boolean }> {
  const values = computeKpiValues(data, null);
  return dashboard
    .filter((d) => !d.restricted)
    .map((d) => {
      const code = values[d.id];
      const sheet = d.sheetValue ?? '';
      const equal = typeof sheet === 'number' && typeof code === 'number' ? Math.abs(sheet - code) <= 1e-12 * Math.max(1, Math.abs(sheet)) : sheet === code;
      return { id: d.id, sheet, code, equal };
    });
}
